/* global GM_STATE */
import { useEffect, useMemo, useState } from 'preact/hooks'
import { formatMoney } from 'libs/shopify'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import register from 'preact-custom-element'
import { SearchspringRecommendProvider } from '@/components/libs/searchspring/fetchers/recommend'
import { GridResults } from '@/components/libs/searchspring/components/GridResults'
import { cart as cartClass } from '@/components/helpers/cart'
import { PromoLine } from '../promo-line/render-promo-line'
// import { Badge } from '../badge/badge-render'
// import { useBadges } from '../badge/use-badge'
import { usePromoLines } from '../promo-line/use-promo-lines'
import { useOmneoClient } from '@/components/libs/omneo/hooks/useOmneoClient'

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p = 'p-2' }) => {
  return (
    <div class={`relative my-auto h-full flex-grow-0 cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        class=" absolute inset-0 h-full w-full cursor-pointer appearance-none border-0 bg-transparent opacity-0"
      />
      <div class={className ?? 'm-auto'}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig }) => {
  const plusIcon = <Icon id="plus" className="min-h-6 min-w-6" />
  const minusIcon = <Icon id="minus" className="min-h-6 min-w-6" />

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = async (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      await new Promise((resolve) => setTimeout(() => resolve(), 1000))
      throw new Error('Unavailable product stock quantity')
    }
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()

      /** Get Action Key */
      const action = e.target.dataset.action

      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)

      if (action === actionKeys.PLUS) {
        /** Throw an error is the new quantity is over the available stock */
        await handleLineItemQuantityChange(item, newQuantity)
      }

      /** Update line item quantity */
      await cartClass.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )
      /** Poll Shopify for new Cart item and set cart config state */
      await cartClass.getCart()
      handleSetCartConfig(CartConfigKeys.cart, cartClass.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <form onChange={onLineItemQuantityChange} class="flex flex-row justify-between pt-4">
      <div>
        <div class="flex flex-row rounded-lg border border-solid border-neutrals-medium-grey">
          <OnChangeTrigger type={actionKeys.MINUS} value={minusIcon} />
          <div class="paragraph-lg w-8 flex-grow p-2 text-center">{item.quantity}</div>
          <OnChangeTrigger type={actionKeys.PLUS} value={plusIcon} />
        </div>
      </div>
      <OnChangeTrigger
        p="p-2 pr-0"
        type={actionKeys.REMOVE}
        value="Remove"
        className={
          'btn btn-text-black btn-sm btn-inline paragraph-sm pointer-events-none text-right underline underline-offset-4 opacity-70'
        }
      />
    </form>
  )
}

const MiniCartMessage = ({ message, onSuccessMessage, goalReached, children }) => {
  const Message = ({ message }) => {
    return <p class="paragraph-xs text-left" dangerouslySetInnerHTML={{ __html: message }}></p>
  }

  const SuccessMessage = ({ onSuccessMessage }) => {
    return (
      <div className={'flex flex-row gap-2'}>
        <span>
          <Icon id="tick" className="w-6 h-6" />
        </span>
        <p class="paragraph-xs text-left" dangerouslySetInnerHTML={{ __html: onSuccessMessage }}></p>
      </div>
    )
  }

  return (
    <div class="bg-primary-grey perspective-0">
      <div data-role="free_shipping_monitor" class="flex -rotate-1 flex-col gap-2 bg-secondary-yellow/20 p-4">
        {goalReached ? <SuccessMessage onSuccessMessage={onSuccessMessage} /> : <Message message={message} />}
        {!goalReached && children}
      </div>
    </div>
  )
}

/**
 *
 *
 * Mini Cart Free Shipping,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartFreeShipping({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }
  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const progressBarPercentage = Math.floor((cartSubTotal / freeShippingMinimum) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <MiniCartMessage
      onSuccessMessage={'You get free standard shipping on this order.'}
      message={`You're <b>${formatMoney(reachedGoal)} away</b> from free shipping`}
      goalReached={progressBarPercentageWidth >= 100}
    >
      <div class="relative h-1 w-full bg-primary-grey">
        <div
          style={{ width: progressBarPercentageWidth + '%' }}
          class="absolute left-0 h-full bg-secondary-yellow transition-all"
        ></div>
        <div class="absolute bottom-0 right-0 top-0 m-auto h-6 w-6 rounded-full bg-primary-grey">
          <Icon id="truck" className="translate-x-1 translate-y-1 min-h-6 min-w-6" />
        </div>
      </div>
    </MiniCartMessage>
  )
}

function CartRewardsBanner({ cartSubTotal, isMiniCart = true, cart }) {
  const giftCardTotal = useMemo(() => cart.items?.reduce((acc, item) => {
    if (item.handle.includes('gift-card')) {
      return acc + item.line_price
    }
    return acc
  }, 0), [cart?.items, cartSubTotal])

  const rewardReceiveAmount = useMemo(() => (cartSubTotal - giftCardTotal) * ((GM_STATE?.rewards?.percentage ?? 10) / 100), [cartSubTotal, giftCardTotal])

  const { client } = useOmneoClient()
  const [rewardBalance, setRewardBalance] = useState(0) // in cents

  useEffect(() => {
    if (!client?.client) return
    refreshBalance()
  }, [client])

  const refreshBalance = () => {
    setTimeout(() => {
      client.profileClient.getBalance().then((data) => {
        if (!data || typeof data.reward_balance !== 'number') return
        setRewardBalance(data.reward_balance * 100)
      })
    }, 2000)
  }

  useEffect(() => {
    if (!client?.client) return
    window.addEventListener('omneo:refresh-balance', refreshBalance)

    return () => {
      window.removeEventListener('omneo:refresh-balance', refreshBalance)
    }
  }, [client])

  if (rewardReceiveAmount <= 0) return null

  const isLoggedIn = !!GM_STATE?.customer?.logged

  return (
    <div class="bg-primary-grey perspective-0">
      <div
        data-role="free_shipping_monitor"
        class="!hidden flex -rotate-1 flex-row items-center gap-2 bg-secondary-violet/20 px-6 py-3"
      >
        {isLoggedIn && (
          <span>
            {isMiniCart ? (
              <Icon id="tick" className="w-6 h-6 text-secondary-violet" />
            ) : (
              <Icon id="patch-rewards" className="w-6 h-6" />
            )}
          </span>
        )}
        {isLoggedIn ? (
          <p className="flex-1 paragraph-xs">
            You'll receive a <b>{formatMoney(rewardReceiveAmount).replace(/\.00$/, '')}</b> Fit Club reward for this
            order.
          </p>
        ) : (
          <p className="flex-1 paragraph-xs">
            Fit Club members can earn a <b>{formatMoney(rewardReceiveAmount).replace(/\.00$/, '')}</b> reward for this
            order.
          </p>
        )}

        {!isLoggedIn && (
          <a
            href="#"
            class="btn btn-text-black btn-sm btn-inline paragraph-sm my-0 h-auto font-bold underline"
            onClick={(e) => {
              e.preventDefault()
              document.querySelector('.mini-cart-close-button')?.click()
              window.openLoginDrawer()
            }}
          >
            Join Fit Club
          </a>
        )}
      </div>
      {isLoggedIn && rewardBalance > 0 && (
        <div
          data-role="free_shipping_monitor"
          class="flex -rotate-1 flex-row items-center gap-2 bg-secondary-green/10 px-6 py-3"
        >
          <span>
            {isMiniCart ? (
              <Icon id="tick" className="w-6 h-6 text-secondary-green" />
            ) : (
              <Icon id="patch-cash" className="w-6 h-6" />
            )}
          </span>
          <p className="flex-1 paragraph-xs">
            You have <b>{formatMoney(rewardBalance).replace(/\.00$/, '')}</b> of Fit Club rewards available to
            spend.
          </p>
        </div>
      )}
    </div>
  )
}

function CartPageRewardsBanner() {
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const { isLoading, cart } = cartConfig

  return <CartRewardsBanner cartSubTotal={cart.items_subtotal_price} isMiniCart={false} cart={cart} />
}

/**
 *
 * MiniCart modal Top Banner
 *
 * @param {\} param0
 * @returns
 */
const MiniCartHeader = ({ itemCount }) => {
  const heading = 'Bag'
  return (
    <div class="border-b-solid sticky top-0 z-20 flex items-center justify-between bg-primary-grey  py-2 pl-6 pr-3">
      <h4 class="h4">
        {heading} ({itemCount})
      </h4>

      <span class="mini-cart-close-button cursor-pointer p-4" data-modal-parent="mini-cart" data-modal-role="close">
        <Icon id="close" className="min-h-6 min-w-6" />
      </span>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const DiscountedPrice = ({ newPrice, oldPrice }) => {
    return (
      <>
        <p class="paragraph-md font-bold">{formatMoney(newPrice)}</p>
        <s class="paragraph-sm text-right opacity-50">{formatMoney(oldPrice)}</s>
      </>
    )
  }

  if (item.discounts.length > 0) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.compare_at_price * item.quantity} />
  }
  return <p class="paragraph-md font-bold">{formatMoney(item.original_line_price)}</p>
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts.map((discount) => {
        return (
          <p class="paragraph-xs w-fit -rotate-1 bg-secondary-yellow px-2 py-1 font-bold uppercase text-primary-black">
            {discount.title}
          </p>
        )
      })}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} className="flex-grow-0 w-48 overflow-hidden rounded-md aspect-1 max-h-36">
      <PreactImage
        imageClass="fit object-bottom"
        className={'h-full w-full object-cover'}
        key={id}
        imageUrl={[imageSrc]}
      />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const productTitle = () => {
    const title = item?.product_title
    const lastIndex = title?.lastIndexOf(' ')

    return {
      title: title?.slice(0, lastIndex),
      colour: title?.slice(lastIndex + 1, title?.length)
    }
  }
  return (
    <div class="flex flex-row justify-between">
      <div class="flex flex-col gap-2">
        <MiniCartItemDiscount item={item} />

        <div>
          <p class="paragraph-md w-4/5 font-bold">{productTitle()?.title}</p>
          <p class="paragraph-sm text-neutrals-secondary-grey">Colour: {productTitle()?.colour}</p>
          {item.options_with_values.map((option) => (
            <p class="paragraph-sm text-neutrals-secondary-grey">
              {option.name === 'Size' ? (
                <>
                  {option.name}: {getSize(item)}
                </>
              ) : (
                <>
                  {option.name}: {option.value}
                </>
              )}
            </p>
          ))}
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <ItemPrice item={item} />
      </div>
    </div>
  )
}

const MiniCartLineItem = ({ item, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-45'
  const criteria = {
    tags: item?.tags ?? [],
    productIds: [item.product_id],
    collectionIds: item.collections.map((c) => c.id.toString())
  }

  // const [badge] = useBadges(criteria)
  const [promoLine] = usePromoLines(criteria)

  return (
    <>
      <div class={`flex flex-row gap-2 px-4 py-6 transition-all ${loadingState}`}>
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />

        {/* <Badge badge={badge} /> */}
        <div class="flex w-full flex-grow flex-col justify-between md:pl-2">
          {promoLine && (
            <div class={'pb-2'}>
              <PromoLine promoline={promoLine} />
              {/* <Badge badge={badge} /> */}
            </div>
          )}

          <MiniCartItemDetails item={item} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
        </div>
      </div>
    </>
  )
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, totalSubTotal: _totalSubTotal, isLoading }) => {
  const cartTotal = formatMoney(total)
  const totalDiscount = formatMoney(totalCartDiscount)
  const totalSubTotal = formatMoney(_totalSubTotal)
  const [view, setView] = useState(false)
  const toggleView = () => setView((t) => !t)

  const open = <Icon id="chevron-down" className="min-h-6 min-w-6" />
  const close = <Icon id="chevron-up" className="min-h-6 min-w-6" />

  const TotalDisplay = ({ title, isLoading, total, render }) => {
    if (render === false) return
    return (
      <div class="flex flex-row justify-between p-4 pb-0">
        <h4 class="h4 font-bold">{title}</h4>
        <h4 class={`h4 font-bold transition-opacity duration-200 ${isLoading && 'opacity-30'}`}>{total}</h4>
      </div>
    )
  }

  const OrderSummaryChild = ({ title, content }) => {
    return (
      <div class="flex flex-row justify-between p-4 pb-0">
        <p class="paragraph-sm ">{title}</p>
        <p class={`paragraph-sm transition-opacity duration-200 ${isLoading && 'opacity-30'}`}>{content}</p>
      </div>
    )
  }
  return (
    <>
      <div data-role="summary" class=" mx-auto flex w-full flex-row justify-center bg-primary-grey py-4">
        <button onClick={toggleView} class="btn btn-solid-black btn-sm drop-shadow-md">
          {view ? 'Hide' : 'Show'} Order Summary <span>{view ? open : close}</span>
        </button>
      </div>
      <div class="flex flex-col justify-between">
        {view && (
          <>
            <OrderSummaryChild title={'Subtotal'} content={totalSubTotal} />
            <OrderSummaryChild title={'Discount'} content={`-${totalDiscount}`} />
            <OrderSummaryChild title={'Shipping'} content={'Calculated at checkout'} />
          </>
        )}
        <TotalDisplay title={'Total'} isLoading={isLoading} total={cartTotal} />
      </div>
    </>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading, emptyDescription }) => {
  const emptyCartDes = 'Your bag is empty'
  const url = GM_STATE.cart.emptyCTALink
  const title = GM_STATE.cart.emptyCTAText

  return (
    <div class={`flex h-full flex-col gap-4 bg-primary-grey p-4 transition-opacity ${isLoading && 'opacity-0'}`}>
      <div className="flex flex-col items-center gap-6 py-10">
        <div className="flex flex-col items-center justify-center gap-2">
          <Icon id="bag-thick" className="w-6 h-6 shrink-0" />
          <h4 class="h4 text-center font-bold">{emptyCartDes}</h4>
        </div>
        <a class="btn btn-lg btn-solid-accent w-fit" href={url}>
          {title ?? 'Continue Shopping'}
        </a>
      </div>
      <CartPopularProducts />
    </div>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div class="p-4 pb-2">
      <a class="btn btn-md btn-solid-accent justify-center" href="/checkout">
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, handleSetCartConfig, isLoading }) => {
  return (
    <div className="pt-4 overflow-scroll max-h-96 bg-primary-grey" data-role="cart_items">
      {cart.items.map((item) => {
        return <MiniCartLineItem item={{ ...item }} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Display load state of the mini cart
 *
 * @param {*} param0
 * @returns
 */
const CartLoading = ({ isLoading }) => {
  return isLoading && <LoaderSpin wrapperClass="bg-transparent" />
}

const getSize = (item) => {
  const sizes = item.variant_title.split('/')
  const size = item.tags.find((t) => t.includes('x-sizing'))
  if (!size) {
    const firstSize = sizes.shift().trimEnd()
    return firstSize.split(' ').pop()
  }
  const [_, country] = size.split(':')
  const selectedSize = sizes.find((s) => s.includes(country))
  if (!selectedSize) {
    return sizes.pop()
  }
  return selectedSize.trimEnd().split(' ').pop()
}

const CartPopularProducts = () => {
  const { cartConfig } = useCartGlobal()
  const productIds = cartConfig.cart.items.map((item) => item.product_id).join(',')

  const emptyCollectionTitle = GM_STATE.cart.emptyCollectionTitle
  const emptyCollectionProfile = GM_STATE.cart.emptyCollectionProfile

  if (!emptyCollectionProfile) return null

  return (
    <SearchspringRecommendProvider profile={emptyCollectionProfile} limit="4" productId={productIds}>
      <GridResults sectionId={emptyCollectionProfile} sectionName={emptyCollectionTitle} title={emptyCollectionTitle} />
    </SearchspringRecommendProvider>
  )
}

function addCartEventListeners(handleSetCartConfig) {
  const handleCartUpdateEvent = async () => {
    try {
      handleSetCartConfig(CartConfigKeys.isLoading, true)

      await cartClass.getCart()
      handleSetCartConfig(CartConfigKeys.cart, cartClass.value)
    } catch (error) {
      console.error('Failed to update cart:', error)
    } finally {
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  // Remove any existing listeners first to prevent duplication
  document.removeEventListener(cartClass.CART_UPDATE_EVENT, handleCartUpdateEvent)

  // Add the event listener
  document.addEventListener(cartClass.CART_UPDATE_EVENT, handleCartUpdateEvent)
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
function MiniCart() {
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = GM_STATE.cart.freeShippingMinimum

  useEffect(() => addCartEventListeners(handleSetCartConfig), [])

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    const checkoutContainer = document.querySelector('shopify-accelerated-checkout-cart')
    if (!checkoutContainer) return

    let popularProductsWrapper = document.querySelector('[data-cart-popular-products]')
    if (!popularProductsWrapper) {
      popularProductsWrapper = document.createElement('div')
      popularProductsWrapper.setAttribute('data-cart-popular-products', '')
      checkoutContainer.after(popularProductsWrapper)
    }

    if (cart.items.length > 0) {
      const popularProducts = document.createElement('mini-cart-popular-products')
      popularProductsWrapper.innerHTML = ''
      popularProductsWrapper.appendChild(popularProducts)
    }
  }, [cart.items.length])

  const totalFullPrice = useMemo(() => {
    return cart.items.reduce((total, item) => {
      const itemFullPrice = item?.compare_at_price || item.price
      return total + itemFullPrice * item.quantity
    }, 0)
  }, [cart])

  const totalDiscount = useMemo(() => {
    return totalFullPrice - cart.total_price
  }, [cart, totalFullPrice])

  return (
    <>
      <MiniCartHeader itemCount={cart.item_count} />
      <CartLoading isLoading={isLoading} />
      <MiniCartFreeShipping cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
      <CartRewardsBanner cartSubTotal={cart.items_subtotal_price} cart={cart} />
      {cart?.items?.length ? (
        <>
          <CartLineItems cart={cart} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          <>
            <DisplayCartTotal
              totalSubTotal={totalFullPrice}
              totalCartDiscount={totalDiscount}
              total={cart.total_price}
              isLoading={isLoading}
            />
            <CheckoutButton checkoutButtonTitle={GM_STATE.cart.checkoutButtonTitle} />
          </>
        </>
      ) : (
        <>
          <EmptyCart isLoading={isLoading} emptyDescription={GM_STATE.cart.emptyDescription} />
        </>
      )}
    </>
  )
}
register(MiniCart, 'mini-cart')
register(CartPopularProducts, 'mini-cart-popular-products')
register(CartPageRewardsBanner, 'cart-rewards-banner')
